import React, { useCallback } from "react";
import { Box } from "@mui/material";
import { StriimMessageBox, StriimTypography } from "@striim/striim-ui-v2";
import { useAppSelector, useAppDispatch } from "src/stores/redux/hooks";
import { getAppModel, getAppStatus, getIsAutomatedGroup, getGroup } from "../helpers/selectors";
import {
    setShowAppErrorsSidebarAction,
    setLoadLocalErrorsAction,
    closeAllSidebarsAction
} from "src/stores/redux/features/flowdesigner/flowdesigner.slice";
import AppControl from "app/components/common/app-control/app-control";
import { moveToAppGroups } from "../helpers/app.utils";
import navigateTo from "src/navigate-to";

interface HaltExceptionMessageBoxProps {
    appName?: string;
    flowName?: string;
    isAutomatedGroup?: boolean;
    moveToAppGroups?: () => void;
}

const styles = {
    container: {
        padding: "8px 16px"
    },
    haltedMessageWrapper: {
        display: "flex",
        justifyContent: "space-between",
        fontFamily: "Inter",
        fontSize: 14,
        fontWeight: 400,
        lineHeight: "20px",
        letterSpacing: 0.14
    },
    haltedMessageLink: {
        textTransform: "none",
        fontWeight: 500,
        cursor: "pointer",
        color: "#00A7E5"
    }
};

const HaltExceptionMessageBox: React.FC<HaltExceptionMessageBoxProps> = ({ appName, flowName }) => {
    const dispatch = useAppDispatch();
    const app = useAppSelector(getAppModel);
    const appStatus = useAppSelector(getAppStatus);
    const isAutomatedGroup = useAppSelector(getIsAutomatedGroup);
    const group = useAppSelector(getGroup);

    const handleResumeClick = useCallback(() => {
        if (app) {
            const appControl = new AppControl({ app });
            appControl.resume();
        }
    }, [app]);

    const handleViewDetailsClick = useCallback(() => {
        dispatch(closeAllSidebarsAction({ appName, flowName }));
        dispatch(setShowAppErrorsSidebarAction(true));
        dispatch(setLoadLocalErrorsAction(false)); // Load fresh from server
        navigateTo.AppErrors(appName);
    }, [dispatch, appName, flowName]);

    // Only show when status is HALT
    if (appStatus !== "HALT") return null;

    return (
        <Box sx={styles.container} data-test-id="exception-msg-box">
            <StriimMessageBox
                text={
                    <div style={styles.haltedMessageWrapper}>
                        {isAutomatedGroup ? (
                            <span>
                                To edit the application, undeploy the automated pipeline. Once all the exceptions have
                                been resolved, resume the pipeline.{" "}
                                <Box
                                    component={StriimTypography}
                                    variant="textButton"
                                    onClick={() => moveToAppGroups(group)}
                                    sx={styles.haltedMessageLink}
                                >
                                    Go to the Automated Pipeline
                                </Box>
                            </span>
                        ) : (
                            <span>
                                The application was halted due to exceptions. Please fix them and{" "}
                                <Box
                                    component={StriimTypography}
                                    variant="textButton"
                                    onClick={handleResumeClick}
                                    sx={styles.haltedMessageLink}
                                    id="resume"
                                    data-test-id="resume"
                                >
                                    resume
                                </Box>{" "}
                                the app.{" "}
                                <Box
                                    component={StriimTypography}
                                    variant="textButton"
                                    onClick={handleViewDetailsClick}
                                    sx={styles.haltedMessageLink}
                                    id="viewDtls"
                                    data-test-id="viewDtls"
                                >
                                    View Details
                                </Box>
                            </span>
                        )}
                    </div>
                }
                type="WARNING"
            />
        </Box>
    );
};

export default HaltExceptionMessageBox;
